import AddMember from "./partial/AddMember";
import React, { useState, useEffect } from "react";
import Icon from '@mdi/react';
import { mdiTrashCanOutline } from '@mdi/js'
import { toggleModal } from "../utils/uiUtils";
import { _saveNewGroupMember, _updateMemberDisplayName, _removeMemberFromGroup, _toggleAdmin } from "../data/groupMembers";
import { _toggleInactiveUser } from "../data/groups";
import { db, firebaseObserver, auth } from "../utils/firebase";
import { collection, onSnapshot } from "firebase/firestore";
import Loading from "./partial/Loading";
import FailureModal from "./partial/FailureModal";
import LoadingModal from "./partial/LoadingModal";
import { useCurrentGroupMembersStore } from "../stores/useCurrentGroupMembersStore";
import { useEnsureGroupData } from "../hooks/useEnsureGroupData";

const GroupMembers = ({ groupId }) => {
    useEnsureGroupData(groupId);

    const [isLoading, setIsLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [displayMembers, setDisplayMembers] = useState();
    const [removeMemberName, setRemoveMemberName] = useState();

    const memberRef = collection(db, 'groups', groupId, 'members');

    const _setGroupAdmins = useCurrentGroupMembersStore((state) => state.setGroupAdmins);
    const _updateMemberOnSnapshot = useCurrentGroupMembersStore((state) => state.updateMemberOnSnapshot);
    const groupMembers = useCurrentGroupMembersStore((state) => state.groupMembers.members);
    const groupAdmins = useCurrentGroupMembersStore((state) => state.groupMembers.admins);

    const toggleInactiveUsers = async (showInactive) => {
        if (showInactive) {
            setDisplayMembers(groupMembers);
        } else {
            setDisplayMembers(Object.keys(groupMembers).filter(memberId => !groupMembers[memberId].inactive).reduce((obj, memberId) => {
                obj[memberId] = groupMembers[memberId];
                return obj;
            }, {}));
        }
    }

    const toggleInactiveUser = async (memberId, active) => {
        _toggleInactiveUser(groupId, memberId, active);
    }


    useEffect(() => {
        setIsAdmin(groupAdmins?.includes(auth.currentUser.uid));
    }, [groupAdmins]);

    useEffect(() => {
        if (!groupMembers) return;

        if (document.getElementById("showInactive")?.checked) {
            setDisplayMembers(groupMembers);
        } else {
            setDisplayMembers(Object.keys(groupMembers).filter(memberId => !groupMembers[memberId].inactive).reduce((obj, memberId) => {
                obj[memberId] = groupMembers[memberId];
                return obj;
            }, {}));
        }

        setIsLoading(false);
    }, [groupMembers]);

    useEffect(() => {
        const trackMembers = onSnapshot(memberRef, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                _updateMemberOnSnapshot(groupId, change.doc.id, change.doc.data(), change.type);
            });
        });

        return () => {
            trackMembers(); // Unsubscribe from the snapshot listener when the component unmounts
        };
    }, []);

    const toggleChangeMemberName = (memberId) => {
        document.getElementById("changeMemberNameId").value = memberId;
        document.getElementById("changeMemberNameTo").value = groupMembers[memberId].displayName;
        toggleModal("modal-changeMemberName");
    }

    const saveNewMemberName = async () => {
        var memberId = document.getElementById("changeMemberNameId").value;
        var newMemberName = document.getElementById("changeMemberNameTo").value;
        var response = await _updateMemberDisplayName(groupId, memberId, newMemberName);
        toggleModal("modal-changeMemberName");
        if (!response) {
            toggleModal("modal-error");
        }
    }

    const removeMemberConfirm = (memberId) => {
        setRemoveMemberName(groupMembers[memberId].displayName);
        document.getElementById("removeMemberId").value = memberId;
        toggleModal("modal-confirmRemoveMember");
    }

    const removeMember = async () => {
        var memberId = document.getElementById("removeMemberId").value;
        toggleModal("modal-confirmRemoveMember");
        toggleModal("loadingModal");
        var response = await _removeMemberFromGroup(groupId, memberId);
        if (!response.data.success) {
            toggleModal("modal-error");
        }
        toggleModal("loadingModal");
    }

    const toggleAdmin = async (modalId, grantAdmin) => {
        var memberId = document.getElementById("toggleAdmin_MemberId").value;
        toggleModal(modalId);
        toggleModal("loadingModal");
        var newGroupAdmins = await _toggleAdmin(groupId, memberId, grantAdmin, groupAdmins);
        _setGroupAdmins(newGroupAdmins);
        toggleModal("loadingModal");
        setDisplayMembers({ ...displayMembers });
        return;
    }

    const hasOtherLoggedInMembers = displayMembers && Object.keys(displayMembers).some(
        memberId => !groupMembers[memberId].isMe && groupMembers[memberId].hasLogin
    );

    const toggleAdminConfirmation = async (memberId, modalId) => {
        document.getElementById("toggleAdmin_MemberId").value = memberId;
        toggleModal(modalId);
    }

    if (isLoading) {
        return <Loading />
    }


    return (
        <>
            <header className="has-text-centered mb-4">
                <p className="subtitle">Group Members</p>
            </header>
            <div className="has-text-centered is-fullwidth">
                <AddMember addMember={_saveNewGroupMember} groupId={groupId} buttonClass="is-primary is-rounded is-small is-outlined mr-5" />
            </div>
            <div className="has-text-centered is-fullwidth">
                <input type="checkbox" id="showInactive" className="mr-2 mt-5 mb-0" onClick={(e) => toggleInactiveUsers(e.currentTarget.checked)} /> Show Inactive Members
            </div>
            <div className="box has-background-info-95 has-wrap-anywhere mt-2">
                <div className="columns is-mobile mb-0">
                    <div className="column is-5 has-text-weight-semibold">Name</div>
                    <div className={"column is-3 has-text-weight-semibold has-text-centered " + (isAdmin ? "" : "is-hidden")}>Inactive</div>
                    {hasOtherLoggedInMembers &&
                        <>
                            <div className={"column is-2 has-text-weight-semibold has-text-centered " + (isAdmin ? "" : "is-hidden")}>Remove</div>
                        </>
                    }
                </div>
                {displayMembers && Object.keys(displayMembers).sort((a, b) => { return displayMembers[a].displayName.localeCompare(displayMembers[b].displayName) }).map((memberId, index) => {
                    return (
                        <div key={"memberList_" + index} className="columns is-mobile mb-0">
                            <div className="column is-5">
                                {groupMembers[memberId].isMe === false && !isAdmin ? (
                                    <div>{groupMembers[memberId].displayName} </div>
                                ) : (
                                    <div className="is-clickable is-underlined has-text-link" onClick={() => toggleChangeMemberName(memberId)}>
                                        {groupMembers[memberId].displayName}
                                    </div>
                                )}
                            </div>
                            <div className={"column is-3 has-text-centered " + (isAdmin ? "" : "is-hidden")}>
                                <input type="checkbox" className={(groupMembers[memberId].isOwner ? "is-hidden" : "")} checked={groupMembers[memberId].inactive} onChange={(event) => { toggleInactiveUser(memberId, event.currentTarget.checked) }} />
                            </div>
                            {isAdmin && !groupMembers[memberId].isOwner && groupMembers[memberId].hasLogin && !groupMembers[memberId].isMe &&
                                <>
                                    <div className="column is-2 has-text-centered ">
                                        <Icon path={mdiTrashCanOutline} size={1} color="black" onClick={() => { removeMemberConfirm(memberId); }} />
                                    </div>
                                    <div className="column is-2 has-text-centered" >
                                        {groupAdmins.includes(groupMembers[memberId].uid) ?
                                            <div className="is-clickable has-text-link is-underlined" onClick={() => { toggleAdminConfirmation(memberId, "modal-toggleRemoveAdmin") }}>
                                                Remove Admin
                                            </div>
                                            :
                                            <div className="is-clickable has-text-link is-underlined" onClick={() => { toggleAdminConfirmation(memberId, "modal-toggleMakeAdmin") }}>
                                                Make Admin
                                            </div>
                                        }
                                    </div>
                                </>
                            }
                        </div>
                    )
                })}
            </div>

            {/* Begin Modals */}
            <div id="modal-groupMemberInfo" className="modal">
                <div className="modal-background"></div>
                <div className="modal-card">
                    <header className="modal-card-head has-text-centered">
                        <p className="modal-card-title">Group Members</p>
                        <button className="delete" aria-label="close" onClick={() => { toggleModal('modal-groupMemberInfo') }}></button>
                    </header>
                    <section className="modal-card-body">
                        <div className="is-size-6">
                            <li>The member list is used primarily to pre-fill the names for the games you log</li>
                            <li>Any member of the group can add a member </li>
                            <li>Members can be removed by only by an admin of the group</li>
                            <li>When you send the ivite link to members and they join they can link their name to their account</li>
                        </div>
                    </section>
                    <footer className="modal-card-foot">
                        <button className="button" aria-label="close" onClick={() => { toggleModal('modal-groupMemberInfo') }}>Close</button>
                    </footer>
                </div>
            </div>

            <div id="modal-changeMemberName" className="modal">
                <div className="modal-background"></div>
                <div className="modal-card">
                    <header className="modal-card-head has-text-centered">
                        <p className="modal-card-title">Change Member Name</p>
                        <button className="delete" aria-label="close" onClick={() => { toggleModal('modal-changeMemberName') }}></button>
                    </header>
                    <section className="modal-card-body">
                        <div className="is-size-6">
                            This will change the display name for this member inside this group only.
                        </div>
                        <div>
                            <input type="hidden" id="changeMemberNameId"></input>
                            <input type="text" className="input is-rounded" maxLength="50" id="changeMemberNameTo" placeholder="test"></input>
                        </div>
                    </section>
                    <footer className="modal-card-foot">
                        <button
                            className="button is-primary mr-2"
                            onClick={async () => { saveNewMemberName() }}>
                            Save New Name
                        </button>
                        <button className="button" aria-label="close" onClick={() => { toggleModal('modal-changeMemberName') }}>Close</button>
                    </footer>
                </div>
            </div>

            <div id="modal-confirmRemoveMember" className="modal">
                <div className="modal-background"></div>
                <div className="modal-card">
                    <header className="modal-card-head has-text-centered">
                        <p className="modal-card-title">Remove Member</p>
                        <button className="delete" aria-label="close" onClick={() => { toggleModal('modal-confirmRemoveMember') }}></button>
                    </header>
                    <section className="modal-card-body">
                        <div className="is-size-6 has-text-weight-bold content">
                            Removing a member breaks the connection between someone's login and the member. This means:
                            <ul>
                                <li>They will lose access to all group content, including play statistics.</li>
                                <li>They will need a new invitation link to rejoin in the future.</li>
                                <li>The member's name will still appear in the list of members and you can log games with them.</li>
                            </ul>

                        </div>
                        <div className="is-size-6 has-text-weight-bold mt-2">
                            If you do not wish them appear in the
                            member list and be available for logging games, also mark them Inactive. Members are not fully deleted because their names may be on historical games
                            and removing them would leave an "Unknown".
                        </div>
                        <div className="mt-5 has-text-danger has-text-weight-bold">
                            <input type="hidden" id="removeMemberId"></input>
                            Are you sure you wish to remove member:  {removeMemberName}
                        </div>
                    </section>
                    <footer className="modal-card-foot">
                        <button
                            className="button is-danger mr-2"
                            onClick={async () => { removeMember() }}>
                            Remove Member
                        </button>
                        <button className="button" aria-label="close" onClick={() => { toggleModal('modal-confirmRemoveMember') }}>Close</button>
                    </footer>
                </div>
            </div>

            {/* This is kept outside the modals as it is used by both Make Admin and Remove Admin */}
            <input type="hidden" id="toggleAdmin_MemberId" />

            <div id="modal-toggleRemoveAdmin" className="modal">
                <div className="modal-background"></div>
                <div className="modal-card">
                    <header className="modal-card-head has-text-centered">
                        <p className="modal-card-title">Remove Admin</p>
                        <button className="delete" aria-label="close" onClick={() => { toggleModal('modal-toggleRemoveAdmin') }}></button>
                    </header>
                    <section className="modal-card-body">
                        <div className="is-size-6 has-text-weight-bold content">
                            You are about to remove this member's Admin access. This means they can no longer:
                            <ul>
                                <li>change members' display names.</li>
                                <li>change the group name.</li>
                                <li>remove other members from the admin list (not the group owner).</li>
                            </ul>
                        </div>
                        <div className="mt-5 has-text-danger has-text-weight-bold">
                            <input type="hidden" id="removeMemberId"></input>
                            Are you sure you wish to remove them as an admin?
                        </div>
                    </section>
                    <footer className="modal-card-foot">
                        <button
                            className="button is-danger mr-2"
                            onClick={async () => { toggleModal('modal-toggleRemoveAdmin'); toggleAdmin("toggleAdmin_MemberId", false) }}>
                            Remove Admin
                        </button>
                        <button className="button is-primary is-outlined" aria-label="close" onClick={() => { toggleModal('modal-toggleMakeAdmin') }}>Close</button>
                    </footer>
                </div>
            </div>

            <div id="modal-toggleMakeAdmin" className="modal">
                <div className="modal-background"></div>
                <div className="modal-card">
                    <header className="modal-card-head has-text-centered">
                        <p className="modal-card-title">Make Admin</p>
                        <button className="delete" aria-label="close" onClick={() => { toggleModal('modal-toggleMakeAdmin') }}></button>
                    </header>
                    <section className="modal-card-body">
                        <div className="is-size-6 has-text-weight-bold content">
                            You are about to make this member an Admin in the group. This means:
                            <ul>
                                <li>They can change members' display names.</li>
                                <li>They can change the group name.</li>
                                <li>They can remove other members from the admin list (not the group owner).</li>
                            </ul>
                        </div>
                        <div className="mt-5 has-text-danger has-text-weight-bold">
                            <input type="hidden" id="removeMemberId"></input>
                            Are you sure you wish to make {removeMemberName} an admin?
                        </div>
                    </section>
                    <footer className="modal-card-foot">
                        <button
                            className="button is-warning mr-2"
                            onClick={async () => { toggleModal('modal-toggleMakeAdmin'); toggleAdmin("toggleAdmin_MemberId", true) }}>
                            Grant Admin
                        </button>
                        <button className="button is-primary is-outlined" aria-label="close" onClick={() => { toggleModal('modal-toggleMakeAdmin') }}>Close</button>
                    </footer>
                </div>
            </div>

            <FailureModal message={"Looks like that action didn't work. Make sure you have permissions and try again"} />
            <LoadingModal />
        </>
    )
}

export default GroupMembers;